/**
 * Party Ledgers Page
 * 
 * Unified interface for viewing customer and supplier ledgers.
 * Features:
 * - Select party (customer or supplier) from dropdown
 * - View ledger transactions
 * - Export to Excel and PDF
 * 
 * Requirements: Party Management System
 * Accessibility: WCAG 2.1 AA compliant with keyboard navigation and ARIA labels
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Autocomplete,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
} from '@mui/icons-material';
import PageHeader from '../components/ui/PageHeader';
import { useSnackbar } from 'notistack';


// Types
interface Party {
  id: string;
  name: string;
  type: 'customer' | 'supplier';
  balance: number;
}

interface LedgerEntry {
  id: string;
  transaction_date: string;  // Changed from 'date'
  description: string;
  reference_number: string;  // Changed from 'reference'
  debit_amount: number;      // Changed from 'debit'
  credit_amount: number;     // Changed from 'credit'
  balance: number;
}

const PartyLedgersPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [partyType, setPartyType] = useState<'customer' | 'supplier'>('customer');
  const [selectedParty, setSelectedParty] = useState<Party | null>(null);
  const [parties, setParties] = useState<Party[]>([]);
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Fetch parties when party type changes
  useEffect(() => {
    fetchParties();
  }, [partyType]);

  // Fetch ledger when party is selected
  useEffect(() => {
    if (selectedParty) {
      fetchLedger();
    } else {
      setLedgerEntries([]);
    }
  }, [selectedParty]);

  /**
   * Fetch parties based on selected type
   */
  const fetchParties = async () => {
    setLoading(true);
    try {
      const endpoint = partyType === 'customer'
        ? `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/customers`
        : `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/suppliers`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch parties');

      const data = await response.json();
      const formattedParties: Party[] = data.items.map((item: any) => ({
        id: item.id,
        name: partyType === 'customer' ? item.name : item.business_name,
        type: partyType,
        balance: item.outstanding_balance || 0,
      }));

      setParties(formattedParties);
      setSelectedParty(null); // Reset selection when type changes
    } catch (error) {
      console.error('Error fetching parties:', error);
      enqueueSnackbar('Failed to load parties', { variant: 'error' });
      setParties([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch ledger entries for selected party
   */
  const fetchLedger = async () => {
    if (!selectedParty) return;

    setLoadingLedger(true);
    try {
      const endpoint = selectedParty.type === 'customer'
        ? `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/customer-ledger/${selectedParty.id}`
        : `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/supplier-ledger/${selectedParty.id}`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch ledger');

      const data = await response.json();
      setLedgerEntries(data.entries || []);
    } catch (error) {
      console.error('Error fetching ledger:', error);
      enqueueSnackbar('Failed to load ledger', { variant: 'error' });
      setLedgerEntries([]);
    } finally {
      setLoadingLedger(false);
    }
  };

  /**
   * Export ledger to Excel (actually exports as PDF since CSV is not implemented)
   */
  const handleExportExcel = async () => {
    if (!selectedParty) return;

    setExporting(true);
    try {
      // Use the direct PDF endpoint since CSV export is not implemented
      const endpoint = selectedParty.type === 'customer'
        ? `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/customer-ledger/${selectedParty.id}/pdf`
        : `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/supplier-ledger/${selectedParty.id}/pdf`;

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to export');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedParty.name}_ledger_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      enqueueSnackbar('Ledger exported successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Error exporting ledger:', error);
      enqueueSnackbar(error.message || 'Failed to export ledger', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  /**
   * Export ledger to PDF
   */
  const handleExportPDF = async () => {
    if (!selectedParty) return;

    setExporting(true);
    try {
      // Use the correct ledger PDF endpoints for both customers and suppliers
      const endpoint = selectedParty.type === 'customer'
        ? `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/customer-ledger/${selectedParty.id}/pdf`
        : `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/supplier-ledger/${selectedParty.id}/pdf`;

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to export PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedParty.name}_ledger_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      enqueueSnackbar('Ledger exported successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Error exporting to PDF:', error);
      enqueueSnackbar(error.message || 'Failed to export ledger', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  /**
   * Format currency
   */
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  /**
   * Format date
   */
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <Box>
      <PageHeader
        title="Party Ledgers"
        subtitle="View financial ledgers for customers and suppliers"
      />

      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        {/* Party Selection Section */}
        <Stack spacing={3}>
          {/* Party Type Toggle */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Party Type
            </Typography>
            <ToggleButtonGroup
              value={partyType}
              exclusive
              onChange={(_, newType) => newType && setPartyType(newType)}
              aria-label="Party type selection"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="customer" aria-label="Customer">
                <PeopleIcon sx={{ mr: 1 }} />
                Customers
              </ToggleButton>
              <ToggleButton value="supplier" aria-label="Supplier">
                <BusinessIcon sx={{ mr: 1 }} />
                Suppliers
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Party Selection Dropdown */}
          <Box>
            <Autocomplete
              options={parties}
              getOptionLabel={(option) => option.name}
              value={selectedParty}
              onChange={(_, newValue) => setSelectedParty(newValue)}
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Select ${partyType === 'customer' ? 'Customer' : 'Supplier'}`}
                  placeholder="Search by name..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <Typography>{option.name}</Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: option.balance >= 0 ? 'success.main' : 'error.main',
                          fontWeight: 600,
                        }}
                      >
                        {formatCurrency(option.balance)}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              sx={{ width: '100%' }}
            />
          </Box>

          {/* Export Buttons */}
          {selectedParty && (
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<ExcelIcon />}
                onClick={handleExportExcel}
                disabled={exporting || loadingLedger}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                }}
              >
                Export to Excel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PdfIcon />}
                onClick={handleExportPDF}
                disabled={exporting || loadingLedger}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                }}
              >
                Export to PDF
              </Button>
            </Stack>
          )}
        </Stack>

        {/* Ledger Display Section */}
        {selectedParty && (
          <Box sx={{ mt: 4 }}>
            {/* Party Summary Card */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
              }}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {selectedParty.name}
                    </Typography>
                    <Chip
                      label={selectedParty.type === 'customer' ? 'Customer' : 'Supplier'}
                      size="small"
                      color={selectedParty.type === 'customer' ? 'primary' : 'secondary'}
                      sx={{ borderRadius: 4 }}
                    />
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Current Balance
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: selectedParty.balance >= 0 ? 'success.main' : 'error.main',
                      }}
                    >
                      {formatCurrency(selectedParty.balance)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Ledger Table */}
            {loadingLedger ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : ledgerEntries.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No ledger entries found for this party.
              </Alert>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: '#0d47a1',
                        '& th': {
                          color: '#ffffff',
                          fontWeight: 600,
                        },
                      }}
                    >
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell align="right">Debit</TableCell>
                      <TableCell align="right">Credit</TableCell>
                      <TableCell align="right">Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ledgerEntries.map((entry) => (
                      <TableRow
                        key={entry.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(33, 150, 243, 0.04)',
                          },
                        }}
                      >
                        <TableCell>{formatDate(entry.transaction_date)}</TableCell>
                        <TableCell>{entry.description}</TableCell>
                        <TableCell>{entry.reference_number}</TableCell>
                        <TableCell align="right">
                          {entry.debit_amount > 0 ? formatCurrency(entry.debit_amount) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {entry.credit_amount > 0 ? formatCurrency(entry.credit_amount) : '-'}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 600,
                            color: entry.balance >= 0 ? 'success.main' : 'error.main',
                          }}
                        >
                          {formatCurrency(entry.balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Empty State */}
        {!selectedParty && (
          <Box
            sx={{
              mt: 4,
              p: 6,
              textAlign: 'center',
              backgroundColor: 'rgba(33, 150, 243, 0.04)',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Select a Party to View Ledger
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a customer or supplier from the dropdown above to view their financial transactions
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PartyLedgersPage;
